"use client";

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Building2,
  RefreshCw,
  HardDrive,
  CreditCard,
  Settings,
  HelpCircle,
  ShieldCheck,
  ChevronLeft,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import BrandLogo from "@/components/BrandLogo";

const mainNavItems = [
  { titleKey: "sidebar.dashboard", url: "/dashboard", icon: LayoutDashboard },
  // { titleKey: "sidebar.tenants", url: "/dashboard/tenants", icon: Building2 },
  // { titleKey: "sidebar.syncMonitoring", url: "/dashboard/sync", icon: RefreshCw },
  { titleKey: "sidebar.manageSynchronization", url: "/dashboard/synchronization", icon: RefreshCw },
  { titleKey: "sidebar.storageAnalytics", url: "/dashboard/storage", icon: HardDrive },
  { titleKey: "sidebar.billing", url: "/dashboard/billing", icon: CreditCard },
  { titleKey: "sidebar.settings", url: "/dashboard/settings", icon: Settings },
  { titleKey: "sidebar.support", url: "/dashboard/support", icon: HelpCircle },
];

const adminNavItems: { titleKey: string; url: string; icon: React.ElementType }[] = [
  // { titleKey: "sidebar.admin", url: "/dashboard/admin", icon: ShieldCheck },
];

interface DashboardSidebarProps {
  isAdmin?: boolean;
}

const DashboardSidebar = ({ isAdmin = true }: DashboardSidebarProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = useLocation().pathname;
  const { t } = useTranslation();

  return (
    <aside
      className={cn(
        "flex flex-col h-screen sticky top-0 border-r border-border/50 bg-[hsl(var(--navy))] text-[hsl(var(--navy-foreground))] transition-all duration-300",
        collapsed ? "w-16" : "w-60"
      )}
    >
      <div className="flex items-center h-14 px-3 border-b border-white/10 gap-1">
        {collapsed ? (
          <button
            onClick={() => setCollapsed(false)}
            className="mx-auto p-1 rounded-md hover:bg-white/10 transition-colors"
            aria-label="Expand sidebar"
          >
            <BrandLogo variant="mark" imgClassName="h-7 w-7" />
          </button>
        ) : (
          <>
            <BrandLogo variant="lockup" onDark imgClassName="h-7 w-7" className="text-sm min-w-0" />
            <button
              onClick={() => setCollapsed(true)}
              className="ml-auto p-1.5 rounded-md hover:bg-white/10 transition-colors shrink-0"
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      <nav className="flex-1 py-4 space-y-1 px-2 overflow-y-auto">
        {mainNavItems.map((item) => {
          const isActive =
            item.url === "/dashboard"
              ? pathname === "/dashboard"
              : (pathname ?? "").startsWith(item.url);
          return (
            <Link
              key={item.url}
              to={item.url}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                isActive
                  ? "bg-primary/20 text-white font-medium"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span>{String(t(item.titleKey))}</span>}
            </Link>
          );
        })}

        {isAdmin && (
          <>
            <div className="my-4 border-t border-white/10" />
            {adminNavItems.map((item) => {
              const isActive = (pathname ?? "") === item.url;
              return (
                <Link
                  key={item.url}
                  to={item.url}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                    isActive
                      ? "bg-primary/20 text-white font-medium"
                      : "text-white/70 hover:bg-white/10 hover:text-white"
                  )}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span>{String(t(item.titleKey))}</span>}
                </Link>
              );
            })}
          </>
        )}
      </nav>

      <div className="px-4 py-3 border-t border-white/10 text-xs text-white/40">
        {!collapsed && "v2.4.1"}
      </div>
    </aside>
  );
};

export default DashboardSidebar;
